// src/pages/Roadmap/HandoffLog.jsx
// STATUS: active | UPDATED: 2026-09-10
// LOG:
//   2026-09-10  New. Two taps on the floor, not a form.
// Logging a step that took two people.
//
// This appears only on steps 3, 7 and 11, which are the ones that cannot be
// performed alone. It is the smallest possible surface on purpose: somebody is
// standing in a showroom with a customer twenty feet away, and anything longer
// than two taps will simply not get done, which would leave the team layer of
// this product built on a table nobody fills in.
//
// THE BLOCKED BUTTON MATTERS AS MUCH AS THE DONE ONE. "I could not get a
// manager" is the most useful row this table can hold, and if the only button
// is a success button then a floor with a real problem looks identical to a
// floor where nobody logged anything.

import { useEffect, useState } from 'react'
import { Box, Button, HStack, Input, Text, VStack, useToast } from '@chakra-ui/react'
import { Check, UserCheck, X } from 'lucide-react'
import { HANDOFF_KINDS, logHandoff, logBlockedHandoff, handoffsToday } from '../../lib/handoffs'

export default function HandoffLog({ stepNumber, userId, dealershipId }) {
  const spec = HANDOFF_KINDS[stepNumber]
  const [today, setToday] = useState([])
  const [saving, setSaving] = useState(false)
  const [blocking, setBlocking] = useState(false)
  const [reason, setReason] = useState('')
  const toast = useToast()

  useEffect(() => {
    if (!userId || !spec) return
    handoffsToday(userId, stepNumber).then(({ data }) => setToday(data))
  }, [userId, stepNumber, spec])

  // A person with no dealership is Jazz or Tyler looking at the step. There is
  // no floor for them to log against, and showing a dead button is worse than
  // showing nothing.
  if (!spec || !userId || !dealershipId) return null

  const record = async (briefed) => {
    setSaving(true)
    const { error } = await logHandoff({ stepNumber, userId, dealershipId, briefed })
    setSaving(false)
    if (error) {
      toast({ status: 'error', title: 'That did not save', description: 'Try again in a moment.' })
      return
    }
    const { data } = await handoffsToday(userId, stepNumber)
    setToday(data)
    toast({ status: 'success', title: briefed ? 'Logged, brief included' : 'Logged' })
  }

  const recordBlocked = async () => {
    if (!reason.trim()) return
    setSaving(true)
    const { error } = await logBlockedHandoff({ stepNumber, userId, dealershipId, reason })
    setSaving(false)
    if (error) {
      toast({ status: 'error', title: 'That did not save', description: 'Try again in a moment.' })
      return
    }
    const { data } = await handoffsToday(userId, stepNumber)
    setToday(data)
    setBlocking(false)
    setReason('')
    toast({ status: 'success', title: 'Noted' })
  }

  const done = today.filter((h) => !h.blocked_reason).length
  const blocked = today.filter((h) => h.blocked_reason).length

  return (
    <Box borderTop="1px solid" borderColor="line" pt={5} mt={5}>
      <Text fontWeight={600} mb={1} letterSpacing="-0.01em">
        {spec.label}
      </Text>
      <Text fontSize="body" color="inkMuted" mb={4} lineHeight={1.6}>
        This step takes two people, so it is the one the whole floor is measured on together. Log it when it
        happens and nobody has to remember at the end of the week.
      </Text>

      {blocking ? (
        <VStack align="stretch" spacing={3}>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What got in the way?"
            maxLength={300}
            autoFocus
          />
          <HStack>
            <Button size="sm" onClick={recordBlocked} isDisabled={!reason.trim()} isLoading={saving}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setBlocking(false); setReason('') }}>
              Cancel
            </Button>
          </HStack>
        </VStack>
      ) : (
        <HStack spacing={2} flexWrap="wrap">
          <Button size="sm" leftIcon={<UserCheck size={15} />} onClick={() => record(true)} isLoading={saving}>
            I briefed them first
          </Button>
          <Button size="sm" variant="outline" leftIcon={<Check size={15} />} onClick={() => record(false)} isDisabled={saving}>
            It happened
          </Button>
          <Button size="sm" variant="ghost" leftIcon={<X size={15} />} onClick={() => setBlocking(true)} isDisabled={saving}>
            Could not
          </Button>
        </HStack>
      )}

      {(done > 0 || blocked > 0) && (
        <Text fontSize="sm" color="inkDim" mt={3}>
          Today: {done > 0 ? `${done} logged` : ''}{done > 0 && blocked > 0 ? ', ' : ''}
          {blocked > 0 ? `${blocked} could not` : ''}.
        </Text>
      )}
    </Box>
  )
}
